import { useState, useCallback, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, FileText, AlertCircle } from 'lucide-react'
import type { ArtefactTemplate } from '../../types/artefact-templates'
import {
  usePlatformTemplates,
  usePlatformTemplateMutations,
  type UsePlatformTemplateMutationsResult,
} from '../../hooks/use-admin-artefact-templates'
import { OrgTemplateCard } from './OrgTemplateCard'
import { VelociraptorConfigModal } from './VelociraptorConfigModal'
import { TestBuildDialog } from './TestBuildDialog'
import { CollectorSettingsDialog } from './CollectorSettingsDialog'

// =============================================================================
// Subcomponents (extracted to keep main component under lint limits)
// =============================================================================

function LoadingSpinner(): ReactElement {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-[#5c6e94]" />
    </div>
  )
}

function ErrorBanner({ message }: { message: string }): ReactElement {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
      <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
        <AlertCircle className="h-4 w-4" />
        <span className="text-sm">{message}</span>
      </div>
    </div>
  )
}

function TemplateListHeader({ onCreateClick }: { onCreateClick: () => void }): ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="flex items-center justify-between">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        {t(
          'velociraptorSettings.collectorTemplates.description',
          'Manage platform-level collector templates for R&D workflows.'
        )}
      </p>
      <button
        onClick={onCreateClick}
        className="inline-flex items-center gap-2 rounded-lg bg-[#5c6e94] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4e5e82]"
      >
        <Plus className="h-4 w-4" />
        {t('velociraptorSettings.collectorTemplates.createTemplate', 'Create Template')}
      </button>
    </div>
  )
}

interface TemplateListProps {
  templates: ArtefactTemplate[]
  deleteConfirmId: string | null
  onEdit: (template: ArtefactTemplate) => void
  onDelete: (id: string) => void
  onDeleteConfirm: (id: string) => void
  onDeleteCancel: () => void
  onTestBuild: (template: ArtefactTemplate) => void
  onCollectorSettings: (template: ArtefactTemplate) => void
}

function TemplateList({
  templates,
  deleteConfirmId,
  onEdit,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
  onTestBuild,
  onCollectorSettings,
}: TemplateListProps): ReactElement {
  const { t } = useTranslation('admin')

  if (templates.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 py-12 dark:border-slate-600">
        <FileText className="h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          {t(
            'velociraptorSettings.collectorTemplates.emptyState',
            'No platform templates yet. Create one to get started.'
          )}
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {templates.map((template) => (
        <OrgTemplateCard
          key={template.id}
          template={template}
          deleteConfirm={deleteConfirmId === template.id}
          onEdit={() => {
            onEdit(template)
          }}
          onDelete={() => {
            onDelete(template.id)
          }}
          onDeleteConfirm={() => {
            onDeleteConfirm(template.id)
          }}
          onDeleteCancel={onDeleteCancel}
          onTestBuild={() => {
            onTestBuild(template)
          }}
          onCollectorSettings={() => {
            onCollectorSettings(template)
          }}
        />
      ))}
    </div>
  )
}

interface TemplateDialogsProps {
  configModalOpen: boolean
  editingTemplate: ArtefactTemplate | null
  mutations: UsePlatformTemplateMutationsResult
  testBuildTemplate: ArtefactTemplate | null
  settingsTemplate: ArtefactTemplate | null
  onCloseConfig: () => void
  onTemplateSaved: () => void
  onCloseTestBuild: () => void
  onCloseSettings: () => void
  onRefetch: () => void
}

interface TemplatesSectionBodyProps {
  templates: ArtefactTemplate[]
  deleteConfirmId: string | null
  mutationsError: string | null
  configModalOpen: boolean
  editingTemplate: ArtefactTemplate | null
  mutations: UsePlatformTemplateMutationsResult
  testBuildTemplate: ArtefactTemplate | null
  settingsTemplate: ArtefactTemplate | null
  onCreateClick: () => void
  onEdit: (template: ArtefactTemplate) => void
  onDelete: (id: string) => void
  onDeleteConfirm: (id: string) => void
  onDeleteCancel: () => void
  onTestBuild: (template: ArtefactTemplate) => void
  onCollectorSettings: (template: ArtefactTemplate) => void
  onCloseConfig: () => void
  onTemplateSaved: () => void
  onCloseTestBuild: () => void
  onCloseSettings: () => void
  onRefetch: () => void
}

function TemplatesSectionBody({
  templates,
  deleteConfirmId,
  mutationsError,
  configModalOpen,
  editingTemplate,
  mutations,
  testBuildTemplate,
  settingsTemplate,
  onCreateClick,
  onEdit,
  onDelete,
  onDeleteConfirm,
  onDeleteCancel,
  onTestBuild,
  onCollectorSettings,
  onCloseConfig,
  onTemplateSaved,
  onCloseTestBuild,
  onCloseSettings,
  onRefetch,
}: TemplatesSectionBodyProps): ReactElement {
  return (
    <div className="space-y-4">
      <TemplateListHeader onCreateClick={onCreateClick} />
      <TemplateList
        templates={templates}
        deleteConfirmId={deleteConfirmId}
        onEdit={onEdit}
        onDelete={onDelete}
        onDeleteConfirm={onDeleteConfirm}
        onDeleteCancel={onDeleteCancel}
        onTestBuild={onTestBuild}
        onCollectorSettings={onCollectorSettings}
      />
      {mutationsError && <ErrorBanner message={mutationsError} />}
      <TemplateDialogs
        configModalOpen={configModalOpen}
        editingTemplate={editingTemplate}
        mutations={mutations}
        testBuildTemplate={testBuildTemplate}
        settingsTemplate={settingsTemplate}
        onCloseConfig={onCloseConfig}
        onTemplateSaved={onTemplateSaved}
        onCloseTestBuild={onCloseTestBuild}
        onCloseSettings={onCloseSettings}
        onRefetch={onRefetch}
      />
    </div>
  )
}

function TemplateDialogs({
  configModalOpen,
  editingTemplate,
  mutations,
  testBuildTemplate,
  settingsTemplate,
  onCloseConfig,
  onTemplateSaved,
  onCloseTestBuild,
  onCloseSettings,
  onRefetch,
}: TemplateDialogsProps): ReactElement {
  return (
    <>
      <VelociraptorConfigModal
        isOpen={configModalOpen}
        onClose={onCloseConfig}
        onTemplateSaved={onTemplateSaved}
        editTemplate={editingTemplate}
        context={{ scope: 'platform', userRole: 'platform_admin' }}
        platformMutations={mutations}
      />
      <TestBuildDialog
        isOpen={testBuildTemplate !== null}
        templateId={testBuildTemplate?.id ?? ''}
        templateName={testBuildTemplate?.name ?? ''}
        onClose={onCloseTestBuild}
        onBuildComplete={onRefetch}
      />
      <CollectorSettingsDialog
        isOpen={settingsTemplate !== null}
        templateId={settingsTemplate?.id ?? ''}
        templateName={settingsTemplate?.name ?? ''}
        onClose={onCloseSettings}
        onSaved={onRefetch}
      />
    </>
  )
}

// =============================================================================
// Main component
// =============================================================================

export function AdminCollectorTemplatesSection(): ReactElement {
  const { templates, loading, error, refetch } = usePlatformTemplates()
  const mutations = usePlatformTemplateMutations()

  const [configModalOpen, setConfigModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<ArtefactTemplate | null>(null)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)
  const [testBuildTemplate, setTestBuildTemplate] = useState<ArtefactTemplate | null>(null)
  const [settingsTemplate, setSettingsTemplate] = useState<ArtefactTemplate | null>(null)

  const handleCreate = useCallback(() => {
    setEditingTemplate(null)
    setConfigModalOpen(true)
  }, [])

  const handleEdit = useCallback((template: ArtefactTemplate) => {
    setEditingTemplate(template)
    setConfigModalOpen(true)
  }, [])

  const handleDelete = useCallback(
    async (templateId: string) => {
      const success = await mutations.remove(templateId)
      if (success) {
        setDeleteConfirmId(null)
        await refetch()
      }
    },
    [mutations, refetch]
  )

  const handleTemplateSaved = useCallback(async () => {
    setConfigModalOpen(false)
    setEditingTemplate(null)
    await refetch()
  }, [refetch])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorBanner message={error} />

  return (
    <TemplatesSectionBody
      templates={templates}
      deleteConfirmId={deleteConfirmId}
      mutationsError={mutations.error}
      configModalOpen={configModalOpen}
      editingTemplate={editingTemplate}
      mutations={mutations}
      testBuildTemplate={testBuildTemplate}
      settingsTemplate={settingsTemplate}
      onCreateClick={handleCreate}
      onEdit={handleEdit}
      onDelete={setDeleteConfirmId}
      onDeleteConfirm={(id) => {
        void handleDelete(id)
      }}
      onDeleteCancel={() => {
        setDeleteConfirmId(null)
      }}
      onTestBuild={setTestBuildTemplate}
      onCollectorSettings={setSettingsTemplate}
      onCloseConfig={() => {
        setConfigModalOpen(false)
        setEditingTemplate(null)
      }}
      onTemplateSaved={() => {
        void handleTemplateSaved()
      }}
      onCloseTestBuild={() => {
        setTestBuildTemplate(null)
      }}
      onCloseSettings={() => {
        setSettingsTemplate(null)
      }}
      onRefetch={() => {
        void refetch()
      }}
    />
  )
}
